import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { STEPS } from "@/data";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "", email: "", age: "",
  program_interest: "", current_situation: "", move_in_timeline: "", additional_info: "",
};

export const Apply = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.program_interest) {
      toast.error("Please fill in your name, email and program interest.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Application submitted! We'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="apply" className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
      <div className="max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A4A4A]">How to Apply</span>
        <h2 className="mt-3 font-heading font-black tracking-tight text-3xl md:text-4xl text-[#0F0F0F] leading-tight">
          Four simple steps to your new home
        </h2>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <div key={s.n} data-testid={`step-${s.n}`} className="bg-white border-2 border-[#0F0F0F]/10 p-6 animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
            <span className="font-heading font-black text-4xl text-[#FFC000]" style={{ WebkitTextStroke: "1.5px #0F0F0F" }}>{s.n}</span>
            <h3 className="mt-3 font-heading font-bold text-base text-[#0F0F0F]">{s.title}</h3>
            <p className="mt-2 text-sm text-[#4A4A4A] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-14 grid lg:grid-cols-5 gap-8 items-start">
        <div className="lg:col-span-3 bg-white border-2 border-[#0F0F0F] p-6 md:p-10 shadow-[6px_6px_0px_#FFC000]">
          {done ? (
            <div data-testid="application-success" className="py-12 text-center">
              <CheckCircle2 size={56} className="mx-auto text-[#FFC000]" strokeWidth={2} />
              <h3 className="mt-4 font-heading font-black text-2xl text-[#0F0F0F]">Application Received!</h3>
              <p className="mt-2 text-[#4A4A4A] max-w-md mx-auto">Thank you for reaching out. A member of our team will contact you within 2 business days to schedule your conversation.</p>
              <button data-testid="apply-again-btn" onClick={() => setDone(false)} className="btn-brutal mt-6">Submit Another Application</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="application-form" className="space-y-6">
              <h3 className="font-heading font-black text-2xl text-[#0F0F0F]">Intake Application</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-wide">Full Name *</Label>
                  <Input data-testid="app-full-name" value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className="mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide">Email *</Label>
                  <Input data-testid="app-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide">Age</Label>
                  <Input data-testid="app-age" value={form.age} onChange={set("age")} placeholder="Optional" className="mt-2" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide">Program of Interest *</Label>
                <Select value={form.program_interest} onValueChange={(v) => setForm({ ...form, program_interest: v })}>
                  <SelectTrigger data-testid="app-program" className="mt-2"><SelectValue placeholder="Select an option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Shared Community Homes">Shared Community Homes</SelectItem>
                    <SelectItem value="Family-Style Residences">Family-Style Residences</SelectItem>
                    <SelectItem value="Not Sure Yet">Not sure yet — need guidance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-wide">Current Situation</Label>
                  <Input data-testid="app-situation" value={form.current_situation} onChange={set("current_situation")} placeholder="Briefly describe" className="mt-2" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide">Desired Move-in Timeline</Label>
                  <Input data-testid="app-timeline" value={form.move_in_timeline} onChange={set("move_in_timeline")} placeholder="e.g. Within 30 days" className="mt-2" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide">Anything else we should know?</Label>
                <Textarea data-testid="app-info" value={form.additional_info} onChange={set("additional_info")} placeholder="Tell us about your goals and needs..." className="mt-2 min-h-[110px]" />
              </div>

              <button type="submit" data-testid="app-submit-btn" disabled={loading} className="btn-brutal w-full sm:w-auto disabled:opacity-60">
                {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Submitting...</> : "Submit Application"}
              </button>
            </form>
          )}
        </div>

        <div className="lg:col-span-2 bg-[#0F0F0F] text-white p-6 md:p-8">
          <ShieldCheck size={28} className="text-[#FFC000]" />
          <h3 className="mt-4 font-heading font-bold text-xl">What to expect</h3>
          <ul className="mt-4 space-y-4 text-sm text-white/80">
            <li className="border-l-2 border-[#FFC000] pl-4">Your application is completely confidential and free.</li>
            <li className="border-l-2 border-[#FFC000] pl-4">No perfect situation required — we welcome everyone.</li>
            <li className="border-l-2 border-[#FFC000] pl-4">A caring advocate reaches out within 2 business days.</li>
            <li className="border-l-2 border-[#FFC000] pl-4">Bring a valid ID and any documents to your intake meeting.</li>
          </ul>
          <p className="mt-6 text-sm text-white/60">Questions? Email us at <span className="text-[#FFC000] font-bold">makeroomforbelievers@gmail.com</span></p>
        </div>
      </div>
    </section>
  );
};
